// Public UI adapted from Klargo-Reference/components/common/RegistrationPlate.tsx.
import React from 'react';
import { formatRegNumber } from '../../lib/format.jsx';
import { cn } from '../../lib/utils.jsx';
import { useLanguage } from '../layout/LanguageSwitcher.jsx';
const LEGACY = {
    lg: 'object',
    md: 'row',
    sm: 'compact',
};
const SINGLE_ROW = {
    hero: {
        border: 2,
        radius: 7,
        band: 20,
        bandText: 9,
        padding: '8px 17px 6px',
        fontSize: 'clamp(26px, 4vw, 34px)',
        tracking: '3px',
        shadow: '0 2px 4px rgba(16,30,40,.14)',
    },
    object: {
        border: 1.5,
        radius: 6,
        band: 17,
        bandText: 8,
        padding: '7px 14px 5px',
        fontSize: 'clamp(22px, 3vw, 28px)',
        tracking: '2.5px',
        shadow: '0 1px 2px rgba(16,30,40,.12)',
    },
    row: {
        border: 1.5,
        radius: 5,
        band: 13,
        bandText: 7,
        padding: '5px 11px 4px',
        fontSize: '19px',
        tracking: '2px',
    },
    compact: {
        border: 1.5,
        radius: 5,
        band: 12,
        bandText: 6.5,
        padding: '4px 10px 3px',
        fontSize: '17px',
        tracking: '1.8px',
    },
    micro: {
        border: 1,
        radius: 4,
        band: 9,
        bandText: 0,
        padding: '2px 8px 1px',
        fontSize: '13px',
        tracking: '1.4px',
    },
};
const TWO_ROW = {
    hero: {
        border: 2,
        radius: 7,
        band: 20,
        bandText: 9,
        padding: '7px 14px 5px',
        fontSize: 'clamp(19px, 2.4vw, 24px)',
        tracking: '2.4px',
        shadow: '0 2px 4px rgba(16,30,40,.14)',
    },
    object: {
        border: 1.5,
        radius: 6,
        band: 14,
        bandText: 7,
        padding: '5px 11px 4px',
        fontSize: '17px',
        tracking: '1.8px',
        shadow: '0 1px 2px rgba(16,30,40,.12)',
    },
    row: {
        border: 1.5,
        radius: 5,
        band: 12,
        bandText: 6.5,
        padding: '4px 9px 3px',
        fontSize: '15px',
        tracking: '1.5px',
    },
    compact: {
        border: 1.5,
        radius: 5,
        band: 11,
        bandText: 6,
        padding: '3px 8px 2px',
        fontSize: '13px',
        tracking: '1.3px',
    },
    micro: {
        border: 1,
        radius: 4,
        band: 9,
        bandText: 0,
        padding: '1px 7px',
        fontSize: '11px',
        tracking: '1.1px',
    },
};
const RegistrationPlate = ({ value, size = 'row', twoRow = false, label, className, }) => {
    const { t } = useLanguage();
    const display = formatRegNumber(value);
    const scale = size === 'lg' || size === 'md' || size === 'sm' ? LEGACY[size] : size;
    const spec = (twoRow ? TWO_ROW : SINGLE_ROW)[scale];
    const [head, ...rest] = display.split(' ');
    const tail = rest.join(' ');
    const canSplit = twoRow && head && tail;
    return (<span role="img" aria-label={label ?? `${t('plate.aria')} ${display}`} className={cn('inline-flex shrink-0 items-stretch overflow-hidden', className)} style={{
            background: 'var(--ds-plate-face)',
            border: `${spec.border}px solid var(--ds-plate-edge)`,
            borderRadius: `${spec.radius}px`,
            boxShadow: spec.shadow,
        }}>
      <span aria-hidden="true" className="flex items-end justify-center font-semibold text-white" style={{
            width: `${spec.band}px`,
            background: 'var(--ds-plate-band)',
            paddingBottom: spec.bandText ? `${Math.max(2, spec.bandText / 2)}px` : undefined,
            fontSize: spec.bandText ? `${spec.bandText}px` : undefined,
        }}>
        {spec.bandText ? 'S' : null}
      </span>
      <span className={cn('whitespace-nowrap font-ds-plate', canSplit && 'flex flex-col items-center')} style={{
            padding: spec.padding,
            fontSize: spec.fontSize,
            letterSpacing: spec.tracking,
            color: 'var(--ds-plate-text)',
            lineHeight: canSplit ? 1.15 : 1,
        }}>
        {canSplit ? (<>
            <span>{head}</span>
            <span>{tail}</span>
          </>) : (display)}
      </span>
    </span>);
};
export default RegistrationPlate;
